function getGigStartValue(gig) {
  return gig?.startsAtUtc || gig?.starts_at_utc || null;
}

function getGigEndValue(gig) {
  return gig?.endsAtUtc || gig?.ends_at_utc || getGigStartValue(gig);
}

function parseDateValue(value) {
  const parsed = value instanceof Date ? new Date(value) : new Date(value || '');
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isHistoricalPerformer(performer) {
  return ['cancelled', 'replaced'].includes(String(performer?.status || '').toLowerCase());
}

export function getGigPerformerSummary(gig) {
  const explicitSummary = String(gig?.performerSummary || gig?.performer_summary || '').trim();
  if (explicitSummary) return explicitSummary;

  const performerNames = Array.isArray(gig?.performerNames)
    ? gig.performerNames.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];
  if (performerNames.length) {
    return performerNames.join(' • ');
  }

  const activePerformers = (Array.isArray(gig?.performers) ? gig.performers : [])
    .filter((performer) => !isHistoricalPerformer(performer))
    .map((performer) => String(performer?.displayName || performer?.display_name || '').trim())
    .filter(Boolean);

  if (!activePerformers.length) return '';
  if (activePerformers.length > 2) {
    return `${activePerformers.slice(0, 2).join(' • ')} +${activePerformers.length - 2} more`;
  }
  return activePerformers.join(' • ');
}

export function buildGigWarnings(gig) {
  const performers = Array.isArray(gig?.performers) ? gig.performers : [];
  const activePerformers = performers.filter((performer) => !isHistoricalPerformer(performer));
  const warnings = [];

  if (!activePerformers.length) {
    warnings.push('No active performers assigned');
  }

  activePerformers.forEach((performer) => {
    const performerName = performer?.displayName || 'Performer';
    if (!performer?.displayName && !performer?.artistId) {
      warnings.push('Unnamed performer in lineup');
    }
    if (!performer?.artworkCreativeId) {
      warnings.push(`${performerName} missing artwork`);
    }
    if (!performer?.confirmed) {
      warnings.push(`${performerName} unconfirmed`);
    }
    if (String(performer?.status || '').toLowerCase() !== 'submitted') {
      warnings.push(`${performerName} still awaiting materials`);
    }
  });

  return Array.from(new Set(warnings));
}

export function formatSchedulerRangeLabel(viewMode, rangeStart, rangeEnd) {
  const start = parseDateValue(rangeStart);
  const end = parseDateValue(rangeEnd);
  if (!start || !end) return 'Invalid range';

  if (viewMode === 'month') {
    return start.toLocaleDateString([], {
      month: 'long',
      year: 'numeric',
    });
  }

  if (viewMode === 'week') {
    const inclusiveEnd = new Date(end.getTime() - 1);
    const sameMonth = start.getMonth() === inclusiveEnd.getMonth() && start.getFullYear() === inclusiveEnd.getFullYear();
    const sameYear = start.getFullYear() === inclusiveEnd.getFullYear();
    const startLabel = start.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
    const endLabel = sameMonth
      ? `${inclusiveEnd.getDate()}, ${inclusiveEnd.getFullYear()}`
      : inclusiveEnd.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
    return `${startLabel} – ${endLabel}`;
  }

  return start.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isRecurringGig(gig) {
  return Number(gig?.recurringTemplateId || gig?.recurring_template_id) > 0;
}

export function getRecurringGigSeriesKey(gig) {
  const recurringTemplateId = Number(gig?.recurringTemplateId || gig?.recurring_template_id || 0);
  if (recurringTemplateId > 0) {
    return `template:${recurringTemplateId}`;
  }
  const gigId = Number(gig?.id || 0);
  return gigId > 0 ? `gig:${gigId}` : null;
}

export function isMultiDayGig(gig) {
  const start = parseDateValue(getGigStartValue(gig));
  const end = parseDateValue(getGigEndValue(gig));
  if (!start || !end) return false;
  return (
    start.getFullYear() !== end.getFullYear() ||
    start.getMonth() !== end.getMonth() ||
    start.getDate() !== end.getDate()
  );
}

export function filterUpcomingGigItems(gigs, filter = 'all', now = new Date()) {
  const nowValue = parseDateValue(now) || new Date();
  return (Array.isArray(gigs) ? gigs : [])
    .filter((gig) => {
      const end = parseDateValue(getGigEndValue(gig));
      return end && end.getTime() >= nowValue.getTime();
    })
    .filter((gig) => {
      if (filter === 'recurring') return isRecurringGig(gig);
      if (filter === 'one-off') return !isRecurringGig(gig);
      if (filter === 'multi-day') return isMultiDayGig(gig);
      if (filter === 'needs-attention') return buildGigWarnings(gig).length > 0;
      return true;
    })
    .sort((left, right) => {
      const leftStart = parseDateValue(getGigStartValue(left))?.getTime() || 0;
      const rightStart = parseDateValue(getGigStartValue(right))?.getTime() || 0;
      return leftStart - rightStart || Number(left?.id || 0) - Number(right?.id || 0);
    });
}

export function buildUpcomingGigSeriesItems(gigs, filter = 'all', now = new Date(), selectedBySeries = {}) {
  const filtered = filterUpcomingGigItems(gigs, filter, now);
  const groups = new Map();
  const nowValue = parseDateValue(now) || new Date();

  filtered.forEach((gig) => {
    const key = getRecurringGigSeriesKey(gig);
    if (!key) return;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(gig);
  });

  return Array.from(groups.entries())
    .map(([seriesKey, seriesGigs]) => {
      const sorted = [...seriesGigs].sort((left, right) => {
        const leftStart = parseDateValue(getGigStartValue(left))?.getTime() || 0;
        const rightStart = parseDateValue(getGigStartValue(right))?.getTime() || 0;
        return leftStart - rightStart || Number(left?.id || 0) - Number(right?.id || 0);
      });
      const selectedGigId = Number(selectedBySeries?.[seriesKey] || 0);
      let currentIndex = sorted.findIndex((gig) => Number(gig?.id || 0) === selectedGigId);
      if (currentIndex < 0) {
        currentIndex = sorted.findIndex((gig) => {
          const end = parseDateValue(getGigEndValue(gig));
          return end && end.getTime() >= nowValue.getTime();
        });
      }
      if (currentIndex < 0) {
        currentIndex = Math.max(0, sorted.length - 1);
      }
      const currentGig = sorted[currentIndex] || null;
      return {
        seriesKey,
        gigs: sorted,
        currentGig,
        currentIndex,
        totalCount: sorted.length,
        hasPrevious: currentIndex > 0,
        hasNext: currentIndex < sorted.length - 1,
        isRecurringSeries: sorted.length > 1 && isRecurringGig(currentGig),
      };
    })
    .filter((entry) => !!entry.currentGig)
    .sort((left, right) => {
      const leftStart = parseDateValue(getGigStartValue(left.currentGig))?.getTime() || 0;
      const rightStart = parseDateValue(getGigStartValue(right.currentGig))?.getTime() || 0;
      return leftStart - rightStart || left.seriesKey.localeCompare(right.seriesKey);
    });
}

function overlapsRange(startValue, endValue, rangeStart, rangeEnd) {
  const start = parseDateValue(startValue);
  const end = parseDateValue(endValue);
  const from = parseDateValue(rangeStart);
  const to = parseDateValue(rangeEnd);
  if (!start || !end || !from || !to) return false;
  return start.getTime() < to.getTime() && end.getTime() > from.getTime();
}

function createDeviceNameMap(devices = []) {
  return new Map(
    devices.map((device) => [
      Number(device?.id),
      device?.name || `Device ${device?.id}`,
    ])
  );
}

export function buildSchedulerListItems({
  gigs = [],
  events = [],
  scheduledOverlays = [],
  devices = [],
  playlists = [],
  playlistCache = {},
  overlays = [],
  overlayCache = {},
  announcements = [],
  announcementCache = {},
  appsByPackage = {},
  rangeStart,
  rangeEnd,
}) {
  const deviceNames = createDeviceNameMap(devices);
  const playlistById = new Map([
    ...playlists.map((playlist) => [Number(playlist?.id), playlist]),
    ...Object.values(playlistCache || {}).map((playlist) => [Number(playlist?.id), playlist]),
  ]);
  const overlayById = new Map([
    ...overlays.map((overlay) => [Number(overlay?.id), overlay]),
    ...Object.values(overlayCache || {}).map((overlay) => [Number(overlay?.id), overlay]),
  ]);
  const announcementById = new Map([
    ...announcements.map((announcement) => [Number(announcement?.id), announcement]),
    ...Object.values(announcementCache || {}).map((announcement) => [Number(announcement?.id), announcement]),
  ]);

  const listItems = [];

  (Array.isArray(gigs) ? gigs : []).forEach((gig) => {
    if (!overlapsRange(getGigStartValue(gig), getGigEndValue(gig), rangeStart, rangeEnd)) return;
    listItems.push({
      id: `gig:${gig.id}`,
      kind: 'gig',
      title: gig?.title || 'Untitled gig',
      subtitle: getGigPerformerSummary(gig) || 'No performer assigned',
      start: getGigStartValue(gig),
      end: getGigEndValue(gig),
      deviceName: 'Gigs',
      status: String(gig?.status || 'draft').toLowerCase(),
      warnings: buildGigWarnings(gig),
    });
  });

  (Array.isArray(events) ? events : []).forEach((event) => {
    if (!overlapsRange(event?.start_time, event?.end_time, rangeStart, rangeEnd)) return;
    const occurrenceKey = event?.occurrence_key || `${event?.id}:${event?.start_time || ''}`;

    const itemType = String(event?.item_type || '').toLowerCase();
    if (itemType === 'playlist' || itemType === 'ad') {
      const playlist = playlistById.get(Number(event?.item_id));
      listItems.push({
        id: `playlist:${occurrenceKey}`,
        kind: itemType === 'ad' ? 'ad' : 'playlist',
        title: playlist?.name || `Playlist ${event?.item_id}`,
        subtitle: itemType === 'ad' ? 'Scheduled playlist ad slot' : 'Scheduled playlist',
        start: event?.start_time,
        end: event?.end_time,
        deviceName: deviceNames.get(Number(event?.device_id)) || 'Device',
        status: null,
        warnings: [],
      });
    }

    if (itemType === 'app') {
      const app = appsByPackage?.[event?.item_id];
      listItems.push({
        id: `app:${occurrenceKey}`,
        kind: 'app',
        title: app?.app_name || app?.package_name || String(event?.item_id || 'App'),
        subtitle: app?.package_name || 'Scheduled app launch',
        start: event?.start_time,
        end: event?.end_time,
        deviceName: deviceNames.get(Number(event?.device_id)) || 'Device',
        status: null,
        warnings: [],
      });
    }
  });

  (Array.isArray(scheduledOverlays) ? scheduledOverlays : []).forEach((item) => {
    if (!overlapsRange(item?.start_time, item?.end_time, rangeStart, rangeEnd)) return;

    const isAd = !!item?.series_id || String(item?.source || '') === 'ad_series';
    const announcement = announcementById.get(Number(item?.announcement_id));
    const overlay = overlayById.get(Number(item?.overlay_id));
    listItems.push({
      id: `${isAd ? 'ad' : 'overlay'}:${item?._key || item?.id}`,
      kind: isAd ? 'ad' : 'overlay',
      title:
        announcement?.title ||
        overlay?.name ||
        `${isAd ? 'Ad' : 'Overlay'} ${item?.overlay_id || item?.announcement_id || item?.id}`,
      subtitle: announcement ? 'Announcement' : overlay?.type || (isAd ? 'Ad slot' : 'Manual overlay'),
      start: item?.start_time,
      end: item?.end_time,
      deviceName: deviceNames.get(Number(item?.device_id)) || 'Device',
      status: null,
      warnings: [],
    });
  });

  return listItems.sort((left, right) => {
    const leftStart = parseDateValue(left.start)?.getTime() || 0;
    const rightStart = parseDateValue(right.start)?.getTime() || 0;
    return leftStart - rightStart || left.title.localeCompare(right.title);
  });
}
